import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { WarehouseAPI } from '../services/api';
import { BundleSKU, BundleCreationState, StockValidationResult, WorkflowStep, ShelfPicking, DraftBundle, InventoryTransaction } from '../types';
import { getAggregatedChildSKUs } from '../utils/inventoryUtils';

// Actions
type BundleCreationAction =
  | { type: 'SELECT_BUNDLE'; payload: BundleSKU }
  | { type: 'SET_QUANTITY'; payload: number }
  | { type: 'SET_VALIDATION_RESULT'; payload: StockValidationResult }
  | { type: 'NEXT_STEP' }
  | { type: 'PREVIOUS_STEP' }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'ADD_SHELF_PICKING'; payload: { skuId: string; shelfLocation: string; quantityToPick: number; availableQuantity: number } }
  | { type: 'REMOVE_SHELF_PICKING'; payload: { skuId: string; shelfLocation: string } }
  | { type: 'UPDATE_SHELF_PICKING'; payload: { skuId: string; shelfLocation: string; quantityToPick: number } }
  | { type: 'SET_CHILD_SKU_PICKED'; payload: { skuId: string; picked: boolean } }
  | { type: 'SET_BUNDLE_SHELF_LOCATION'; payload: string }
  | { type: 'ADD_BUNDLE_STORAGE_LOCATION'; payload: { shelfLocation: string; quantity: number } }
  | { type: 'REMOVE_BUNDLE_STORAGE_LOCATION'; payload: string }
  | { type: 'UPDATE_BUNDLE_STORAGE_LOCATION'; payload: { shelfLocation: string; quantity: number } }
  | { type: 'SAVE_DRAFT_START' }
  | { type: 'SAVE_DRAFT_SUCCESS'; payload: { draftId: string } }
  | { type: 'SAVE_DRAFT_ERROR' }
  | { type: 'LOAD_DRAFT'; payload: DraftBundle }
  | { type: 'UPDATE_INVENTORY'; payload: InventoryTransaction[] }
  | { type: 'SET_UNICOMMERCE_SYNC_STATUS'; payload: boolean }
  | { type: 'RESET_WORKFLOW' };

// Initial state
const initialState: BundleCreationState = {
  selectedBundleSKU: null,
  quantity: 1,
  currentStep: 0,
  validationResult: null,
  childSKUPicking: {},
  allSKUsPicked: false,
  bundleShelfLocation: '',
  bundleStorageLocations: [],
  totalCreated: 0,
  totalRequired: 0,
  isDraft: false,
  draftId: undefined,
  savingDraft: false,
  unicommerceSyncCompleted: false,
};

// Workflow steps
export const workflowSteps: WorkflowStep[] = [
  { id: 0, name: 'Select Bundle', completed: false, active: true },
  { id: 1, name: 'Set Quantity', completed: false, active: false },
  { id: 2, name: 'Validate Stock', completed: false, active: false },
  { id: 3, name: 'Pick Child SKUs', completed: false, active: false },
  { id: 4, name: 'Create Bundle', completed: false, active: false },
];

// Reducer
const bundleCreationReducer = (
  state: BundleCreationState,
  action: BundleCreationAction
): BundleCreationState => {
  switch (action.type) {
    case 'SELECT_BUNDLE':
      return {
        ...state,
        selectedBundleSKU: action.payload,
        childSKUPicking: {},
      };

    case 'SET_QUANTITY':
      return {
        ...state,
        quantity: Math.max(1, action.payload),
        validationResult: null, // Reset validation when quantity changes
      };

    case 'SET_VALIDATION_RESULT':
      // Initialize child SKU picking structure when validation result is set
      const childSKUPicking: { [skuId: string]: { picked: boolean; shelfPickings: ShelfPicking[]; totalRequired: number; totalPicked: number; } } = {};

      if (state.selectedBundleSKU && action.payload.childSKUAvailability) {
        getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).forEach((childSKU: any) => {
          const totalRequired = childSKU.requiredQuantity * state.quantity;
          childSKUPicking[childSKU.skuId] = {
            picked: false,
            shelfPickings: [],
            totalRequired: totalRequired,
            totalPicked: 0
          };
        });
      }

      return {
        ...state,
        validationResult: action.payload,
        childSKUPicking: { ...state.childSKUPicking, ...childSKUPicking },
      };

    case 'NEXT_STEP':
      return {
        ...state,
        currentStep: Math.min(workflowSteps.length - 1, state.currentStep + 1),
      };

    case 'PREVIOUS_STEP':
      return {
        ...state,
        currentStep: Math.max(0, state.currentStep - 1),
      };

    case 'SET_STEP':
      return {
        ...state,
        currentStep: Math.max(0, Math.min(workflowSteps.length - 1, action.payload)),
      };

    case 'ADD_SHELF_PICKING':
      const currentPicking = state.childSKUPicking[action.payload.skuId] || {
        picked: false,
        shelfPickings: [],
        totalRequired: 0,
        totalPicked: 0
      };

      const newShelfPicking: ShelfPicking = {
        shelfLocation: action.payload.shelfLocation,
        quantityToPick: action.payload.quantityToPick,
        availableQuantity: action.payload.availableQuantity
      };

      const updatedPickings = [...currentPicking.shelfPickings, newShelfPicking];
      const newTotalPicked = updatedPickings.reduce((sum, sp) => sum + sp.quantityToPick, 0);

      const updatedChildSKUPicking = {
        ...state.childSKUPicking,
        [action.payload.skuId]: {
          ...currentPicking,
          shelfPickings: updatedPickings,
          totalPicked: newTotalPicked,
          picked: newTotalPicked >= currentPicking.totalRequired
        },
      };

      const allPicked = Object.values(updatedChildSKUPicking).every(p => p.picked);

      return {
        ...state,
        childSKUPicking: updatedChildSKUPicking,
        allSKUsPicked: allPicked,
      };

    case 'REMOVE_SHELF_PICKING':
      const existingPicking = state.childSKUPicking[action.payload.skuId];
      if (!existingPicking) return state;

      const filteredPickings = existingPicking.shelfPickings.filter(
        sp => sp.shelfLocation !== action.payload.shelfLocation
      );
      const updatedTotalPicked = filteredPickings.reduce((sum, sp) => sum + sp.quantityToPick, 0);

      const updatedChildSKUPickingRemove = {
        ...state.childSKUPicking,
        [action.payload.skuId]: {
          ...existingPicking,
          shelfPickings: filteredPickings,
          totalPicked: updatedTotalPicked,
          picked: updatedTotalPicked >= existingPicking.totalRequired
        },
      };

      const allPickedRemove = Object.values(updatedChildSKUPickingRemove).every(p => p.picked);

      return {
        ...state,
        childSKUPicking: updatedChildSKUPickingRemove,
        allSKUsPicked: allPickedRemove,
      };

    case 'UPDATE_SHELF_PICKING':
      const pickingToUpdate = state.childSKUPicking[action.payload.skuId];
      if (!pickingToUpdate) return state;

      const updatedShelfPickings = pickingToUpdate.shelfPickings.map(sp =>
        sp.shelfLocation === action.payload.shelfLocation
          ? { ...sp, quantityToPick: action.payload.quantityToPick }
          : sp
      );
      const finalTotalPicked = updatedShelfPickings.reduce((sum, sp) => sum + sp.quantityToPick, 0);

      const updatedChildSKUPickingUpdate = {
        ...state.childSKUPicking,
        [action.payload.skuId]: {
          ...pickingToUpdate,
          shelfPickings: updatedShelfPickings,
          totalPicked: finalTotalPicked,
          picked: finalTotalPicked >= pickingToUpdate.totalRequired
        },
      };

      const allPickedUpdate = Object.values(updatedChildSKUPickingUpdate).every(p => p.picked);

      return {
        ...state,
        childSKUPicking: updatedChildSKUPickingUpdate,
        allSKUsPicked: allPickedUpdate,
      };

    case 'SET_CHILD_SKU_PICKED':
      return {
        ...state,
        childSKUPicking: {
          ...state.childSKUPicking,
          [action.payload.skuId]: {
            ...state.childSKUPicking[action.payload.skuId],
            picked: action.payload.picked,
          },
        },
      };

    case 'SET_BUNDLE_SHELF_LOCATION':
      return {
        ...state,
        bundleShelfLocation: action.payload,
      };

    case 'ADD_BUNDLE_STORAGE_LOCATION':
      // Check if location already exists
      const existingLocationIndex = state.bundleStorageLocations.findIndex(
        loc => loc.shelfLocation === action.payload.shelfLocation
      );

      if (existingLocationIndex >= 0) {
        // Update existing location quantity
        const updatedLocations = [...state.bundleStorageLocations];
        updatedLocations[existingLocationIndex] = {
          ...updatedLocations[existingLocationIndex],
          quantity: updatedLocations[existingLocationIndex].quantity + action.payload.quantity
        };
        return {
          ...state,
          bundleStorageLocations: updatedLocations,
        };
      } else {
        // Add new location
        return {
          ...state,
          bundleStorageLocations: [...state.bundleStorageLocations, action.payload],
        };
      }

    case 'REMOVE_BUNDLE_STORAGE_LOCATION':
      return {
        ...state,
        bundleStorageLocations: state.bundleStorageLocations.filter(
          loc => loc.shelfLocation !== action.payload
        ),
      };

    case 'UPDATE_BUNDLE_STORAGE_LOCATION':
      return {
        ...state,
        bundleStorageLocations: state.bundleStorageLocations.map(loc =>
          loc.shelfLocation === action.payload.shelfLocation
            ? { ...loc, quantity: action.payload.quantity }
            : loc
        ),
      };

    case 'SAVE_DRAFT_START':
      return {
        ...state,
        savingDraft: true,
      };

    case 'SAVE_DRAFT_SUCCESS':
      return {
        ...state,
        isDraft: true,
        draftId: action.payload.draftId,
        savingDraft: false,
      };

    case 'SAVE_DRAFT_ERROR':
      return {
        ...state,
        savingDraft: false,
      };

    case 'LOAD_DRAFT': {
      // Ensure metadata is an object even if it arrived as a string
      const loadPayload = action.payload as any;
      const payload: DraftBundle = typeof loadPayload === 'string' ? JSON.parse(loadPayload) : loadPayload;

      return {
        ...state,
        selectedBundleSKU: payload.bundleSKU,
        quantity: payload.quantity,
        childSKUPicking: payload.childSKUPicking || {},
        bundleShelfLocation: payload.bundleShelfLocation || '',
        bundleStorageLocations: payload.bundleStorageLocations || [],
        isDraft: true,
        draftId: payload.id,
        unicommerceSyncCompleted: !!payload.unicommerceSyncCompleted,
        validationResult: payload.validationResult || (payload.status === 'picked' || Object.keys(payload.childSKUPicking || {}).length > 0 ? { isValid: true, errors: [], childSKUAvailability: [] } : null),
        allSKUsPicked: payload.status === 'picked',
        currentStep: typeof payload.currentStep === 'number'
          ? payload.currentStep
          : (payload.status === 'picked' && payload.unicommerceSyncCompleted) ? 4 : 3,
      };
    }

    case 'UPDATE_INVENTORY':
      // This action is for tracking inventory changes
      // In a real app, this would trigger inventory updates
      return state;

    case 'SET_UNICOMMERCE_SYNC_STATUS':
      return {
        ...state,
        unicommerceSyncCompleted: action.payload,
      };

    case 'RESET_WORKFLOW':
      return initialState;

    default:
      return state;
  }
};

// Context
interface BundleCreationContextType {
  state: BundleCreationState;
  dispatch: React.Dispatch<BundleCreationAction>;
  canProceedToNextStep: () => boolean;
  getActiveWorkflowSteps: () => WorkflowStep[];
  isWorkflowComplete: () => boolean;
  saveDraft: (syncStatusOverride?: boolean) => Promise<void>;
  loadDraft: (draftId: string) => Promise<void>;
  updateInventory: (transactions: InventoryTransaction[]) => void;
}

const BundleCreationContext = createContext<BundleCreationContextType | undefined>(undefined);

// Provider
interface BundleCreationProviderProps {
  children: ReactNode;
}

export const BundleCreationProvider: React.FC<BundleCreationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(bundleCreationReducer, initialState);

  const canProceedToNextStep = (): boolean => {
    switch (state.currentStep) {
      case 0: // Select Bundle
        return state.selectedBundleSKU !== null;
      case 1: // Set Quantity
        return state.quantity > 0;
      case 2: // Validate Stock
        // Ensure we have a selected bundle and validation result.
        // If we're resuming a draft that's already at the picking stage, we can be more lenient
        // if state.childSKUPicking has data, which indicates validation was previously passed.
        const hasPickingData = Object.keys(state.childSKUPicking).length > 0;
        return state.selectedBundleSKU !== null && (state.validationResult?.isValid === true || hasPickingData);
      case 3: // Pick Child SKUs
        if (!state.selectedBundleSKU) return false;
        const allPicked = getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).every((childSKU: any) => {
          const picking = state.childSKUPicking[childSKU.skuId];
          return picking?.picked && picking.totalPicked >= picking.totalRequired;
        });
        // Require both: all SKUs picked AND Unicommerce sync completed
        return allPicked && state.unicommerceSyncCompleted;
      case 4: // Create Bundle
        return state.bundleShelfLocation.trim() !== '';
      default:
        return false;
    }
  };

  const getActiveWorkflowSteps = (): WorkflowStep[] => {
    return workflowSteps.map((step, index) => ({
      ...step,
      completed: index < state.currentStep,
      active: index === state.currentStep,
    }));
  };

  const isWorkflowComplete = (): boolean => {
    return state.currentStep === workflowSteps.length - 1 && canProceedToNextStep();
  };

  const saveDraft = async (syncStatusOverride?: boolean): Promise<void> => {
    if (!state.selectedBundleSKU) return;

    dispatch({ type: 'SAVE_DRAFT_START' });

    try {
      // Use the override if provided, otherwise use the current state
      const unicommerceSyncCompleted = typeof syncStatusOverride === 'boolean'
        ? syncStatusOverride
        : state.unicommerceSyncCompleted;

      // Create draft bundle object (frontend representation)
      const draftBundle: Partial<DraftBundle> = {
        bundleSKU: state.selectedBundleSKU,
        quantity: state.quantity,
        childSKUPicking: state.childSKUPicking,
        lastUpdated: new Date().toISOString(),
        status: state.allSKUsPicked ? 'picked' : 'partially_picked',
        validationResult: state.validationResult || undefined,
        unicommerceSyncCompleted: unicommerceSyncCompleted,
        bundleShelfLocation: state.bundleShelfLocation || undefined,
        bundleStorageLocations: state.bundleStorageLocations || [],
        currentStep: state.currentStep,
      };

      // Backend representation
      const backendDraftData = {
        bundleSku: state.selectedBundleSKU.name, // Use name or SKU code
        targetQuantity: state.quantity,
        metadata: draftBundle, // Store full state in metadata
        status: 'draft'
      };

      let result;
      if (state.isDraft && state.draftId) {
        // Update existing draft
        result = await WarehouseAPI.updateDraft(state.draftId, backendDraftData);
      } else {
        // Create new draft
        result = await WarehouseAPI.createDraft(backendDraftData);
      }

      dispatch({ type: 'SAVE_DRAFT_SUCCESS', payload: { draftId: result._id || result.id } });
    } catch (error) {
      console.error('Error saving draft to backend:', error);
      dispatch({ type: 'SAVE_DRAFT_ERROR' });
      throw error;
    }
  };

  const loadDraft = async (draftId: string): Promise<void> => {
    try {
      const backendDraft = await WarehouseAPI.getDraftById(draftId);
      if (backendDraft && backendDraft.metadata) {
        // The metadata contains the full DraftBundle state
        const draftBundle: DraftBundle = {
          ...backendDraft.metadata,
          id: backendDraft._id || backendDraft.id,
          createdAt: backendDraft.createdAt,
          lastUpdated: backendDraft.updatedAt || backendDraft.lastUpdated,
        };
        dispatch({ type: 'LOAD_DRAFT', payload: draftBundle });
      } else if (backendDraft) {
        // Fallback if metadata is missing but draft exists
        const fallbackDraft: DraftBundle = {
          id: backendDraft._id || backendDraft.id,
          bundleSKU: { id: '', name: backendDraft.bundleSku, description: '', childSKUs: [] },
          quantity: backendDraft.targetQuantity,
          childSKUPicking: {},
          createdAt: backendDraft.createdAt,
          lastUpdated: backendDraft.updatedAt,
          status: 'partially_picked'
        };
        dispatch({ type: 'LOAD_DRAFT', payload: fallbackDraft });
      }
    } catch (error) {
      console.error('Error loading draft from backend:', error);
      throw error;
    }
  };

  const updateInventory = (transactions: InventoryTransaction[]): void => {
    // In a real app, this would update inventory in backend
    dispatch({ type: 'UPDATE_INVENTORY', payload: transactions });

    // Store inventory transactions for tracking
    const existingTransactions = JSON.parse(localStorage.getItem('inventory-transactions') || '[]');
    const updatedTransactions = [...existingTransactions, ...transactions];
    localStorage.setItem('inventory-transactions', JSON.stringify(updatedTransactions));
  };

  const contextValue: BundleCreationContextType = {
    state,
    dispatch,
    canProceedToNextStep,
    getActiveWorkflowSteps,
    isWorkflowComplete,
    saveDraft,
    loadDraft,
    updateInventory,
  };

  return (
    <BundleCreationContext.Provider value={contextValue}>
      {children}
    </BundleCreationContext.Provider>
  );
};

// Hook
export const useBundleCreation = (): BundleCreationContextType => {
  const context = useContext(BundleCreationContext);
  if (context === undefined) {
    throw new Error('useBundleCreation must be used within a BundleCreationProvider');
  }
  return context;
};